/**
 * Sender-side memory kept in this browser only (senders never log in).
 * - device id: lets the shop inbox group repeat uploads from the same person
 * - active order: lets later uploads join the same tracking link, and lets the
 *   sender get back to tracking after closing the tab
 */

const DEVICE_ID_KEY = 'prindox_device_id';
const ORDER_KEY_PREFIX = 'prindox_order_';

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode / storage blocked: features degrade, uploads still work
  }
};

const safeRemove = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

const randomId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
};

export const getDeviceId = (): string => {
  let id = safeGet(DEVICE_ID_KEY);
  if (!id) {
    id = randomId();
    safeSet(DEVICE_ID_KEY, id);
  }
  return id;
};

export const getActiveOrder = (shopId: string): string | null =>
  shopId ? safeGet(ORDER_KEY_PREFIX + shopId) : null;

export const saveActiveOrder = (shopId: string, orderToken: string) => {
  if (shopId && orderToken) safeSet(ORDER_KEY_PREFIX + shopId, orderToken);
};

export const clearActiveOrder = (shopId: string, orderToken?: string) => {
  if (!shopId) return;
  // Only clear if it is still the same order (a newer one may have started)
  if (orderToken && getActiveOrder(shopId) !== orderToken) return;
  safeRemove(ORDER_KEY_PREFIX + shopId);
};

export const buildTrackUrl = (orderToken: string, shopId?: string | null, shopName?: string | null) => {
  const params = new URLSearchParams({ order: orderToken });
  if (shopId) params.set('shop', shopId);
  if (shopName) params.set('name', shopName);
  return `/track?${params.toString()}`;
};
